import axios from 'axios';
import { JsonResponse } from 'core/models/response';
import { Assembly, AssemblyChairmanCandidatesResponse, AssemblyListResponse, AssemblyVotingSummary, Decision, Staff } from '../models/assemblies';
import { ActionShareholder, ActionUser, VerificationPayload } from '../../commonModels';
import { Attendee, AttendeesListResponse } from '../models/attendance';

export const onGetAssembliesList = async (page: number = 1) => {
	try {
		const response = await axios.get<JsonResponse<AssemblyListResponse>>('dashboard/actions/assemblies', {
			params: { page },
		});
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onGetAssemblyById = async (id: string) => {
	try {
		const response = await axios.get<JsonResponse<Assembly>>(`dashboard/actions/assemblies/${id}`);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onCancelAssembly = async (id: number) => {
	try {
		const response = await axios.post(`dashboard/actions/assemblies/${id}/cancel`);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onCreateAssembly = async (payload: FormData) => {
	try {
		const response = await axios.post(`dashboard/actions/assemblies`, payload, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onUpdateAssembly = async (id: number, payload: FormData) => {
	try {
		const response = await axios.post(`dashboard/actions/assemblies/${id}`, payload, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onSendAssemblyOtp = async () => {
	try {
		const response = await axios.post('dashboard/actions/assemblies/send-otp');
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onSendAssemblyActionOtp = async (type: 'open_voting' | 'open_attendance') => {
	try {
		const response = await axios.post('dashboard/actions/assemblies/send-action-otp', { type });
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onStartVoting = async (id: number, data: any) => {
	try {
		const response = await axios.post(`dashboard/actions/assemblies/${id}/start-voting`, data);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onEndVoting = async (id: number, { key, method }: VerificationPayload) => {
	try {
		const response = await axios.post(`dashboard/actions/assemblies/${id}/end-voting`, { method, key });
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onCloseAssembly = async (id: number, { key, method }: VerificationPayload) => {
	try {
		const response = await axios.post(`dashboard/actions/assemblies/${id}/close-assembly`, { method, key });
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onPublishAssembly = async (id: number) => {
	try {
		const response = await axios.post(`dashboard/actions/assemblies/${id}/publish?publish_type=Public`);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onStartAttendance = async (id: number, data: any) => {
	try {
		const response = await axios.post(`dashboard/actions/assemblies/${id}/start-attendance`, data);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onGetAssemblyAttendees = async (id: string, page: number, keyword?: string) => {
	try {
		const response = await axios.get<JsonResponse<AttendeesListResponse>>(`dashboard/actions/assemblies/${id}/attendees`, {
			params: { page, keyword },
		});
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onGetAssemblyAttendeeById = async (id: string, attendeeId: number) => {
	try {
		const response = await axios.get<JsonResponse<Attendee>>(`dashboard/actions/assemblies/${id}/attendees/${attendeeId}`);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onSearchShareholder = async (id: string, keyword: string) => {
	try {
		const response = await axios.get<JsonResponse<ActionShareholder[]>>(`dashboard/actions/assemblies/${id}/attendees/search`, {
			params: { keyword },
		});
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onAddAttendee = async (id: string, payload: FormData) => {
	try {
		const response = await axios.post<JsonResponse<ActionShareholder[]>>(`dashboard/actions/assemblies/${id}/attendees`, payload, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onGetDecisions = async (assemblyId: string, attendanceId: string) => {
	try {
		const response = await axios.get<JsonResponse<Decision[]>>(
			`dashboard/actions/assemblies/${assemblyId}/voting-decisions?attendance_id=${attendanceId}`,
		);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onAddVote = async (assemblyId: string, payload: FormData) => {
	try {
		const response = await axios.post(`dashboard/actions/assemblies/${assemblyId}/add-vote`, payload, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onGetStaff = async () => {
	try {
		const response = await axios.get<JsonResponse<Staff[]>>(`dashboard/actions/assemblies/staff-with-account-manager`);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onSearchUser = async (search: string) => {
	try {
		const response = await axios.get<JsonResponse<ActionUser[]>>(`dashboard/actions/assemblies/abstention-candidates/search?search=${search}`);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};

export const onGetAssemblyVotingSummary = async (id: string) => {
	try {
		const response = await axios.get<JsonResponse<AssemblyVotingSummary>>(`dashboard/actions/assemblies/${id}/voting-summary`);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};


export const onGetAssemblyChairmanCandidates = async () => {
	try {
		const response = await axios.get<JsonResponse<AssemblyChairmanCandidatesResponse>>(`dashboard/actions/assemblies/chairman-candidates`);
		return response.data.data;
	} catch (error) {
		throw error;
	}
};